/**
 * De foutcontrole van scherm 02: inklapbaar, en standaard dicht zodra alles is
 * goedgekeurd.
 *
 * Goedkeuren haalt de rode markering uit de tabel weg — die volgt uit `rodeVelden` op
 * de materiaalregel, die de server berekent — maar de melding blíjft hier staan, met
 * wie hem heeft goedgekeurd en wanneer. Dat is de expliciete wens: de historie van een
 * afwijking mag niet verdwijnen.
 *
 * Het paneel is alleen zichtbaar voor productievoorbereiding, tekenaars en admin. Voor
 * productie stuurt de API de meldingen niet eens mee.
 */
import { useState } from 'react';

import type { Foutmelding } from '../api';
import { datumTijd } from '../gegevens';
import { IconChevronOnder, IconWaarschuwing } from './Iconen';

const VELDNAAM: Record<string, string> = {
  materiaal: 'Materiaalcode',
  dikte: 'Dikte',
  lengte: 'Lengte',
  breedte: 'Breedte',
  aantal: 'Aantal',
  benaming: 'Benaming',
  maten: 'Maatvoering',
  zijde0: 'Afwerking zijde 0',
  zijde5: 'Afwerking zijde 5',
  zijde1: 'Kant 1_1',
  zijde2: 'Kant 1_2',
  zijde3: 'Kant 2_3',
  zijde4: 'Kant 2_4',
  overmaatLengte: 'Overmaat lengte',
  overmaatBreedte: 'Overmaat breedte',
};

interface Props {
  meldingen: Foutmelding[];
  magGoedkeuren: boolean;
  onGoedkeuren: (melding: Foutmelding, intrekken: boolean) => void;
  bezig: string | null;
}

export function Foutcontrole({ meldingen, magGoedkeuren, onGoedkeuren, bezig }: Props) {
  const rood = meldingen.filter((m) => m.ernst === 'rood');
  const open = rood.filter((m) => !m.goedgekeurd);
  const goedgekeurd = rood.filter((m) => m.goedgekeurd);
  const overig = meldingen.filter((m) => m.ernst !== 'rood');

  /*
   * Standaard open zolang er iets openstaat, en dicht zodra alles is goedgekeurd.
   * Bewust afgeleid van de gegevens en niet één keer bij het opbouwen bepaald: de
   * meldingen komen na de eerste render binnen, en dan zou het paneel dicht blijven
   * terwijl er nog werk ligt. Zodra de gebruiker zelf klapt, wint zijn keuze.
   */
  const [handmatig, setHandmatig] = useState<boolean | null>(null);
  const uitgeklapt = handmatig ?? open.length > 0;

  if (rood.length === 0 && overig.length === 0) return null;

  return (
    <section className="hs-foutpaneel">
      <button
        type="button"
        className="hs-foutpaneel__kop"
        onClick={() => setHandmatig(!uitgeklapt)}
        aria-expanded={uitgeklapt}
      >
        <IconChevronOnder
          maat={13}
          className={uitgeklapt ? undefined : 'hs-chevron-dicht'}
        />
        <IconWaarschuwing maat={14} />
        Foutcontrole
        {open.length > 0 ? <span className="hs-chip hs-chip--fout">{open.length} open</span> : null}
        {goedgekeurd.length > 0 ? (
          <span className="hs-chip hs-chip--goed">{goedgekeurd.length} goedgekeurd</span>
        ) : null}
        {overig.length > 0 ? (
          <span className="hs-chip">{overig.length} waarschuwing{overig.length === 1 ? '' : 'en'}</span>
        ) : null}
        <span className="hs-sectielabel" style={{ marginLeft: 'auto' }}>
          Zichtbaar voor productievoorbereiding &amp; tekenaars
        </span>
      </button>

      {uitgeklapt
        ? [...open, ...goedgekeurd, ...overig].map((melding, i) => (
            <div
              // Eén onderdeel kan twee meldingen met dezelfde sleutel hebben (lengte én
              // breedte buiten de machinegrens); de uitleg verschilt, de sleutel niet.
              key={`${melding.onderdeelId}|${melding.sleutel}|${i}`}
              className={`hs-foutregel${melding.goedgekeurd ? ' hs-foutregel--goedgekeurd' : ''}`}
            >
              <span className="hs-foutregel__nr">nr. {melding.onderdeelNummer}</span>
              <span className="hs-foutregel__veld">{VELDNAAM[melding.veld] ?? melding.veld}</span>
              <span className="hs-foutregel__waarde" title={String(melding.waarde ?? '')}>
                {melding.waarde ?? '—'}
              </span>
              <span className="hs-foutregel__uitleg">
                {melding.goedgekeurd
                  ? `Goedgekeurd door ${melding.goedgekeurd.door} · ${datumTijd(melding.goedgekeurd.op)} — de rode waarde vervalt, de melding blijft staan.`
                  : melding.uitleg}
              </span>
              {melding.ernst === 'rood' && magGoedkeuren ? (
                melding.goedgekeurd ? (
                  <button
                    type="button"
                    className="hs-chip hs-chip--goed"
                    style={{ cursor: 'pointer', border: 0 }}
                    title="Goedkeuring intrekken"
                    disabled={bezig === melding.sleutel}
                    onClick={() => onGoedkeuren(melding, true)}
                  >
                    Goedgekeurd
                  </button>
                ) : (
                  <button
                    type="button"
                    className="hs-knop"
                    style={{ height: 24, padding: '0 9px' }}
                    disabled={bezig === melding.sleutel}
                    onClick={() => onGoedkeuren(melding, false)}
                  >
                    Goedkeuren
                  </button>
                )
              ) : null}
            </div>
          ))
        : null}
    </section>
  );
}
